// GOLDEN PATH CODE
// This is a table backed by open-ended data (ie, we do not know exactly how many pages of data there are)
// Based on https://cloudscape.aws.dev/examples/react/server-side-table.html

import React, { useState } from 'react';
import Table from '@amzn/awsui-components-react/polaris/table';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import Spinner from '@amzn/awsui-components-react/polaris/spinner';
import { PropertyFilterProps } from '@amzn/awsui-components-react/polaris/property-filter';
import Header from '@amzn/awsui-components-react/polaris/header';
import Pagination from '@amzn/awsui-components-react/polaris/pagination';
import Multiselect from '@amzn/awsui-components-react/polaris/multiselect';
import type { MultiselectProps } from '@amzn/awsui-components-react/polaris/multiselect';

import { useTranslation } from 'react-i18next';
import { useTableStrings } from '../../../hooks/localization/tableStrings';
import { usePaginationStrings } from '../../../hooks/localization/paginationStrings';
import { PeoplePropertyFilter } from '../csvAttendance/peoplePropertyFilter';
import { useTableColumns, ColumnMap } from '../../../hooks/tableColumns';
import {
  useAttendeeSessionStorage,
  useAttendeeSessionStorageNonReactive,
} from '../../../hooks/attendance/attendeeSessionStorage';
import { useBulkParticipantSessionStorage } from '../../../hooks/attendance/bulkParticipantSessionStorage';
import { useUser } from '../../../hooks/user';
import { usePaginatedRedVelvetQuery } from '../../../hooks/paginatedRedVelvetQuery';
import {
  Amazonian,
  columnMap,
  getAliases,
  getAmazoniansFromPeople,
  convertTokensToPeopleQuery,
  ParticipationWithFullname,
} from '../../../utils/attendance/peopleSearch';
import { Event, Participation, IdentifierType } from '@amzn/red-velvet-api';
import { useQueryClient } from '@tanstack/react-query';
import { callQueryClientSingletonRedVelvetQuery } from '../../../hooks/singleItemRedVelvetQuery';
import { getEnabledFeatures, useFeatures } from '../../../hooks/features';
import { useSearchParams } from 'react-router-dom';
import { publishKatalMetric } from '../../katalAnalytics';
import { PeoplePreferences } from './peoplePreferences';

export type SearchTableMode = 'default' | 'bulkUpload';

export interface SearchTableProps {
  participants: ParticipationWithFullname[];
  setParticipants: React.Dispatch<React.SetStateAction<ParticipationWithFullname[]>>;
  useNonReactive?: boolean; // Use non-reactive session storage so modal data updates do not trigger page refresh
  onUpdate: () => void;
  mode?: SearchTableMode; // Controls which columns to display
  shiftOptions?: MultiselectProps.Options; // Available shifts for bulk upload mode
  event?: Event; // Event for fetching participations in bulk upload mode
  shiftIdFromTrackAttendanceWizard?: string; // Single shift mode when provided
  isUnlistedEventMode?: boolean; // Unlisted event mode - uses single-shift Actions column without participation fetching
}

export function SearchTable(props: SearchTableProps) {
  const {
    participants,
    useNonReactive = false,
    mode = 'default',
    shiftOptions = [],
    event,
    shiftIdFromTrackAttendanceWizard,
    isUnlistedEventMode = false,
  } = props;

  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);
  const tableStrings = useTableStrings('searchTable', (item: Amazonian) => item.name, namespaces);
  const paginationStrings = usePaginationStrings('searchTable', namespaces);
  const queryClient = useQueryClient();
  const features = useFeatures();
  const [searchParams] = useSearchParams();
  const { bulkParticipants, updateBulkParticipants } = useBulkParticipantSessionStorage();
  const user = useUser();

  // Uses the DID_NOT_ATTEND pattern that the hook already supports for deletion
  const removeBulkParticipant = React.useCallback(
    (alias: string) => {
      if (!bulkParticipants[alias]) return;

      updateBulkParticipants({
        [alias]: {
          ...bulkParticipants[alias],
          attendance: 'DID_NOT_ATTEND',
          signedUp: false,
        },
      });
    },
    [bulkParticipants, updateBulkParticipants]
  );

  const [pageSize, setPageSize] = useState(100);
  const [selectedItems, setSelectedItems] = useState<Amazonian[]>([]);
  const [filterObj, setFilterObj] = useState<PropertyFilterProps.Query>({
    tokens: [],
    operation: 'or',
  });

  const [selectedShiftsByAlias, setSelectedShiftsByAlias] = useState<
    Record<string, MultiselectProps.Options>
  >({});
  const [bulkSelectedShifts, setBulkSelectedShifts] = useState<MultiselectProps.Options>([]);
  const [participationsByAlias, setParticipationsByAlias] = useState<Map<string, Participation[]>>(
    new Map()
  );
  const [isLoadingParticipations, setIsLoadingParticipations] = useState(false);

  const getActionButton = React.useCallback(
    (alias: string): 'add' | 'remove' | 'none' => {
      const isInSessionStorage = bulkParticipants[alias] !== undefined;
      if (isInSessionStorage) {
        return 'remove';
      }

      const userParticipations = participationsByAlias.get(alias) || [];
      const hasParticipations = userParticipations.length > 0;

      if (!hasParticipations) {
        return 'add';
      }

      const participationShiftIds = new Set(
        userParticipations.map((p) => p.shiftId).filter((id): id is string => id !== undefined)
      );
      const selectedShifts = selectedShiftsByAlias[alias] || [];
      const hasNewShifts = selectedShifts.some(
        (opt) => !participationShiftIds.has(opt.value as string)
      );

      return hasNewShifts ? 'add' : 'none';
    },
    [bulkParticipants, participationsByAlias, selectedShiftsByAlias]
  );

  // GOLDEN PATH NOTE
  // We need to save the largest page we have ever visited with this set of queries
  // so that if a user goes from page 5 back to page 1, they can easily hop back to page 5
  const [largestPage, setLargestPage] = useState(1);

  // GOLDEN PATH NOTE
  // The server-side fetching is in charge of tracking/caching/retrieving all the different pagination tokens for the open-ended query
  // and managing the pagination logic.  The component is only in charge of mapping the query result data to the proper format
  const searchQuery = convertTokensToPeopleQuery(filterObj);
  const { pageIndex, setPageIndex, queryResult } = usePaginatedRedVelvetQuery({
    redVelvetQueryKey: ['getPeople', { ...searchQuery, pageSize }],
  });
  const amazonians = React.useMemo(
    () =>
      filterObj.tokens.length > 0 ? getAmazoniansFromPeople(queryResult.data?.people || []) : [],
    [filterObj.tokens.length, queryResult.data?.people]
  );

  // Hook selection based on use case - must be called at component level
  const reactiveActions = useAttendeeSessionStorage();
  const nonReactiveActions = useAttendeeSessionStorageNonReactive();

  // Use appropriate hook if SearchTable is used within a modal or not
  const addAttendees = useNonReactive
    ? nonReactiveActions.addAttendees
    : reactiveActions.addAttendees;

  const aliasesCsv = React.useMemo(() => {
    return amazonians
      .map((a) => a.alias)
      .filter((alias): alias is string => alias !== undefined)
      .sort()
      .join(',');
  }, [amazonians]);

  const eventId = event?.eventId;

  // Fetch participation data for bulk upload mode
  React.useEffect(() => {
    if (mode !== 'bulkUpload' || !eventId || aliasesCsv.length === 0) {
      return;
    }

    const fetchParticipations = async (): Promise<void> => {
      setIsLoadingParticipations(true);

      try {
        const response = await callQueryClientSingletonRedVelvetQuery(
          queryClient,
          {
            redVelvetQueryKey: [
              'getParticipationsByEventAndAttendees',
              {
                eventId: eventId,
                shiftId: shiftIdFromTrackAttendanceWizard, // if undefined, it still works for multi-shift
                attendees: aliasesCsv,
                attendeeIdentifierType: IdentifierType.ALIAS,
                shiftEndedBefore: new Date(),
              },
            ],
          },
          getEnabledFeatures(features, searchParams)
        );

        const participationMap = new Map<string, Participation[]>();

        response.participations?.forEach((p) => {
          if (p.volunteerAlias) {
            const existing = participationMap.get(p.volunteerAlias) || [];
            participationMap.set(p.volunteerAlias, [...existing, p]);
          }
        });

        // trackedParticipations are already tracked and immutable
        response.trackedParticipations?.forEach((p) => {
          if (p.volunteerAlias) {
            const existing = participationMap.get(p.volunteerAlias) || [];
            participationMap.set(p.volunteerAlias, [...existing, p]);
          }
        });

        setParticipationsByAlias(participationMap);
      } catch (error) {
        console.error('Error fetching participations:', error);
      } finally {
        setIsLoadingParticipations(false);
      }
    };

    fetchParticipations();
  }, [
    mode,
    eventId,
    aliasesCsv,
    shiftIdFromTrackAttendanceWizard,
    queryClient,
    features,
    searchParams,
  ]);

  // In unlisted event mode, force single-shift behavior (Actions column) even without a real shiftId
  const useSingleShiftMode = !!shiftIdFromTrackAttendanceWizard || isUnlistedEventMode;

  const activeColumnMap: ColumnMap<Amazonian> =
    mode === 'bulkUpload'
      ? buildBulkUploadColumnMap(
          selectedShiftsByAlias,
          setSelectedShiftsByAlias,
          shiftOptions,
          t,
          props.onUpdate,
          participationsByAlias,
          useSingleShiftMode,
          updateBulkParticipants,
          user,
          removeBulkParticipant,
          isLoadingParticipations,
          getActionButton
        )
      : columnMap;

  // Use different translation keys based on mode to maintain backward compatibility
  const translationKey = mode === 'bulkUpload' ? 'searchTable' : 'attendeeTable';
  const columns = useTableColumns(activeColumnMap, translationKey, namespaces);

  // GOLDEN PATH NOTE
  // We determine pagination/number of items found/etc based on if we have a next page or not
  // If there is a next page, we need to indicate to the user that there are an unknown number of results
  // If there is not a next page, we know exactly how many items there are
  const isOpenEnded = !queryResult.isLoading && queryResult.data?.page !== undefined;
  const foundItems = isOpenEnded
    ? pageSize * largestPage + '+'
    : pageSize * (pageIndex - 1) + (amazonians.length || 0);

  const combineAttendees = (
    amazonians: Amazonian[],
    participants: ParticipationWithFullname[]
  ): string[] => {
    const participantsAliases = getAliases(participants);
    const amazonianAliases = getAliases(amazonians).filter((a) => !participantsAliases.includes(a));

    return amazonianAliases;
  };

  return (
    <Table
      header={
        mode === 'default' ? (
          <Header
            actions={
              <Button
                variant="primary"
                disabled={selectedItems.length === 0}
                onClick={() => {
                  const searchAliases = combineAttendees(selectedItems, participants);
                  addAttendees(searchAliases);
                  // to update the modal on attendees changes
                  props.onUpdate();
                  setSelectedItems([]);
                  publishKatalMetric('attendance_addAttendees', searchAliases.length.toString());
                }}
              >
                {t('searchTable.coordinatorTracking.addAttendees')}
              </Button>
            }
            counter={
              selectedItems.length ? `(${selectedItems.length}/${foundItems})` : `(${foundItems})`
            }
            description={t('searchTable.coordinatorTracking.trackNonSignedupAttendance')}
            variant="h3"
          >
            {t('searchTable.title')}
          </Header>
        ) : (
          <Header
            counter={`(${foundItems})`}
            description={t('searchTable.bulkUpload.description')}
            variant="h3"
            actions={
              useSingleShiftMode ? (
                // Single shift mode (including unlisted events): action button to add selected users
                (() => {
                  const addableItems = selectedItems.filter((item) => {
                    if (!item.alias) return false;
                    const actionType = getActionButton(item.alias);
                    return actionType === 'add';
                  });

                  return (
                    <Button
                      variant="primary"
                      disabled={addableItems.length === 0}
                      onClick={() => {
                        const aliases = addableItems
                          .map((item) => item.alias)
                          .filter((alias): alias is string => alias !== undefined);

                        const bulkUpdate: Record<string, Amazonian> = {};
                        aliases.forEach((alias) => {
                          const selectedUser = amazonians.find((a) => a.alias === alias);
                          if (selectedUser) {
                            bulkUpdate[alias] = {
                              alias: selectedUser.alias,
                              name: selectedUser.name,
                              building: selectedUser.building,
                              managerLogin: selectedUser.managerLogin,
                              signedUp: false,
                              attendance: 'ATTENDED',
                              trackedTime: 0,
                              trackedBy: user.userId,
                            };
                          }
                        });

                        updateBulkParticipants(bulkUpdate);
                        props.onUpdate();
                        setSelectedItems([]);
                        publishKatalMetric(
                          'attendance_bulkAddSingleShiftFromCheckbox',
                          addableItems.length.toString()
                        );
                      }}
                    >
                      {t('searchTable.bulkUpload.addSelectedToList', {
                        count: addableItems.length,
                      })}
                    </Button>
                  );
                })()
              ) : (
                // Multi-shift mode: dropdown to bulk-apply shifts to selected rows
                <Multiselect
                  selectedOptions={bulkSelectedShifts}
                  onChange={({ detail }) => {
                    setBulkSelectedShifts(detail.selectedOptions);

                    const aliases = selectedItems
                      .map((item) => item.alias)
                      .filter((alias): alias is string => alias !== undefined);

                    const updatedSelections: Record<string, MultiselectProps.Options> = {};
                    aliases.forEach((alias) => {
                      const userParticipations = participationsByAlias.get(alias) || [];
                      const participationShiftIds = new Set(
                        userParticipations
                          .map((p) => p.shiftId)
                          .filter((id): id is string => id !== undefined)
                      );

                      // Keep locked shifts (existing participations) and add new selections
                      const lockedShifts = shiftOptions.filter((opt) =>
                        participationShiftIds.has(opt.value as string)
                      );

                      const newShifts = detail.selectedOptions.filter(
                        (opt) => !participationShiftIds.has(opt.value as string)
                      );

                      updatedSelections[alias] = [...lockedShifts, ...newShifts];
                    });

                    setSelectedShiftsByAlias((prev) => ({
                      ...prev,
                      ...updatedSelections,
                    }));
                  }}
                  onBlur={() => {
                    setBulkSelectedShifts([]);
                    setSelectedItems([]);
                  }}
                  options={shiftOptions}
                  placeholder={t('searchTable.bulkUpload.selectShiftsForSelected', {
                    count: selectedItems.length,
                  })}
                  disabled={selectedItems.length === 0}
                  expandToViewport
                />
              )
            }
          >
            {t('searchTable.title')}
          </Header>
        )
      }
      filter={
        <PeoplePropertyFilter
          query={filterObj}
          onChange={({ detail }) => {
            // GOLDEN PATH NOTE
            // When we change the filter, we need to reset all pagination and selected items back to their inital state
            const metricValue = detail.tokens
              .map((token) => `${token.propertyKey || 'fuzzySearch'}:${token.value}`)
              .join(',');
            publishKatalMetric('attendance_searchPeopleFilter', metricValue);
            setSelectedItems([]);
            setPageIndex(1);
            setLargestPage(1);
            setFilterObj(detail);
          }}
        />
      }
      pagination={
        <Pagination
          ariaLabels={paginationStrings}
          currentPageIndex={pageIndex}
          onChange={({ detail }) => {
            // GOLDEN PATH NOTE
            // Using Math.max allows us to auto-set the largest page when we visit it, and leave it alone
            // if we are re-visiting a previous page
            setLargestPage(Math.max(detail.currentPageIndex, largestPage));
            setPageIndex(detail.currentPageIndex);
          }}
          openEnd={isOpenEnded}
          // GOLDEN PATH NOTE
          // We always list the largest page as the page count, even if we are on the last page and know there is
          // a next page, because the users will use the next arrow to load the next page
          pagesCount={largestPage}
        />
      }
      preferences={
        <PeoplePreferences
          onConfirm={({ detail }) => setPageSize(detail.pageSize || 100)}
          preferences={{
            pageSize,
          }}
        />
      }
      onSelectionChange={({ detail }) => setSelectedItems(detail.selectedItems)}
      selectedItems={selectedItems}
      selectionType="multi"
      {...tableStrings}
      loading={queryResult.isLoading}
      empty={
        <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
          <b>
            {queryResult.isError
              ? queryResult.error.name.startsWith('4')
                ? t('searchTable.badRequestMessage')
                : t('searchTable.errorOccurred')
              : t('searchTable.noneFound')}
          </b>
        </Box>
      }
      {...columns}
      items={amazonians || []}
      trackBy="alias"
      data-testid={'people-search-table'}
    />
  );
}

/**
 * Builds column map for bulk upload mode.
 * Single shift mode (including unlisted events): Actions column.
 * Multi-shift mode: Shifts Attended column.
 */
function buildBulkUploadColumnMap(
  selectedShiftsByAlias: Record<string, MultiselectProps.Options>,
  setSelectedShiftsByAlias: React.Dispatch<
    React.SetStateAction<Record<string, MultiselectProps.Options>>
  >,
  shiftOptions: MultiselectProps.Options,
  t: (key: string) => string,
  onUpdate: () => void,
  participationsByAlias: Map<string, Participation[]>,
  useSingleShiftMode: boolean,
  updateBulkParticipants: (participants: Record<string, Amazonian>) => void,
  user: { userId: string },
  removeBulkParticipant: (alias: string) => void,
  isLoadingParticipations: boolean,
  getActionButton: (alias: string) => 'add' | 'remove' | 'none'
): ColumnMap<Amazonian> {
  const baseColumns = {
    ...columnMap,
  };

  // Single shift mode (including unlisted events): Actions column instead of Shifts Attended
  if (useSingleShiftMode) {
    return {
      ...baseColumns,
      actions: (item: Amazonian) => {
        if (!item.alias) {
          return '';
        }

        if (isLoadingParticipations) {
          return <Spinner />;
        }

        const actionType = getActionButton(item.alias);

        if (actionType === 'none') {
          return '';
        }

        if (actionType === 'remove') {
          return (
            <Button
              variant="normal"
              onClick={() => {
                if (!item.alias) return;
                removeBulkParticipant(item.alias);
                onUpdate();
                publishKatalMetric('attendance_removeFromSearchInBulkUpload', item.alias);
              }}
            >
              {t('searchTable.bulkUpload.remove')}
            </Button>
          );
        }

        return (
          <Button
            variant="normal"
            onClick={() => {
              if (!item.alias) return;

              const newParticipant: Amazonian = {
                alias: item.alias,
                name: item.name,
                building: item.building,
                managerLogin: item.managerLogin,
                signedUp: false,
                attendance: 'ATTENDED',
                trackedTime: 0,
                trackedBy: user.userId,
              };

              updateBulkParticipants({ [item.alias]: newParticipant });
              onUpdate();
              publishKatalMetric('attendance_addAttendeeFromSearchInBulkUpload', item.alias);
            }}
          >
            {t('searchTable.bulkUpload.addToAttendanceList')}
          </Button>
        );
      },
    };
  }

  // Multi-shift mode: Shifts Attended column
  return {
    ...baseColumns,
    shiftsAttended: (item: Amazonian) => {
      if (!item.alias) return '';

      if (isLoadingParticipations) {
        return <Spinner />;
      }

      const userParticipations = participationsByAlias.get(item.alias) || [];
      const participationShiftIds = new Set(
        userParticipations.map((p) => p.shiftId).filter((id): id is string => id !== undefined)
      );

      let currentSelections = selectedShiftsByAlias[item.alias];
      if (!currentSelections) {
        // Initialize with participation shifts (locked)
        currentSelections = shiftOptions
          .filter((opt) => participationShiftIds.has(opt.value as string))
          .map((opt) => ({
            ...opt,
            iconName: 'lock-private',
          }));
      } else {
        // Add lock icon to locked shifts in current selections
        currentSelections = currentSelections.map((opt) => ({
          ...opt,
          iconName: (participationShiftIds.has(opt.value as string)
            ? 'lock-private'
            : undefined) as 'lock-private' | undefined,
        }));
      }

      // Show all shifts with lock icons for existing participations
      const availableOptions = shiftOptions.map((opt) => {
        const isLocked = participationShiftIds.has(opt.value as string);
        return {
          ...opt,
          iconName: (isLocked ? 'lock-private' : undefined) as 'lock-private' | undefined,
          disabled: isLocked,
        };
      });

      return (
        <Multiselect
          selectedOptions={currentSelections}
          onChange={({ detail }) => {
            if (!item.alias) return;

            // Ensure locked shifts remain selected (cannot be deselected)
            const lockedOptions = currentSelections.filter((opt) =>
              participationShiftIds.has(opt.value as string)
            );
            const newOptions = detail.selectedOptions.filter(
              (opt) => !participationShiftIds.has(opt.value as string)
            );
            const finalSelection = [...lockedOptions, ...newOptions];

            setSelectedShiftsByAlias((prev) => ({
              ...prev,
              [item.alias as string]: finalSelection,
            }));
          }}
          options={availableOptions}
          placeholder={t('searchTable.bulkUpload.selectShift')}
          expandToViewport
        />
      );
    },
  };
}
